import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { ConnectedIdentity } from './types'
import { Link2, Lock, Shield, Trash2 } from 'lucide-react'
import { GoogleIcon, MicrosoftIcon } from '@/components/icons'

interface SecurityCardProps {
  identities: ConnectedIdentity[]
  errors?: Record<string, string[]>
  onPasswordChange: (payload: { password: string; password_confirmation: string }) => void
  onDisconnect: (id: string) => void
}

export function SecurityCard({ identities, errors, onPasswordChange, onDisconnect }: SecurityCardProps) {
  const [passwordData, setPasswordData] = useState({ password: '', password_confirmation: '' })
  const [isSavingPassword, setIsSavingPassword] = useState(false)

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSavingPassword(true)
    onPasswordChange(passwordData)
    setIsSavingPassword(false)
  }

  const renderProviderBadge = (identity: ConnectedIdentity) => {
    if (identity.provider_key === 'google_oauth2') {
      return <GoogleIcon className="size-5" />
    }
    if (identity.provider_key === 'microsoft_graph') {
      return <MicrosoftIcon className="size-5" />
    }
    return (
      <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">
        {identity.provider.slice(0, 1)}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="size-5 text-muted-foreground" />
            <div>
              <CardTitle>Set Password</CardTitle>
              <CardDescription>Enable email/password login for your account</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handlePasswordSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Enter your new password"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  required
                />
                {errors?.password && (
                  <p className="text-sm text-destructive">{errors.password[0]}</p>
                )}
              </div>
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Confirm your new password"
                  value={passwordData.password_confirmation}
                  onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                  required
                />
                {errors?.password_confirmation && (
                  <p className="text-sm text-destructive">{errors.password_confirmation[0]}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" className="cursor-pointer" disabled={isSavingPassword}>
                {isSavingPassword ? 'Saving...' : 'Set Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="size-5 text-muted-foreground" />
            <div>
              <CardTitle>Connected Accounts</CardTitle>
              <CardDescription>Social accounts linked to your profile for easy sign-in</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {identities.length > 0 ? (
            <div className="divide-y divide-border rounded-lg border border-border">
              {identities.map((identity) => (
                <div key={identity.id} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    {renderProviderBadge(identity)}
                    <div>
                      <p className="font-medium">{identity.provider}</p>
                      <p className="text-xs text-muted-foreground">{identity.identifier || 'No identifier yet'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>
                      {identity.connected_at
                        ? `Connected ${identity.connected_at}`
                        : 'Awaiting connection'}
                    </span>
                    <span className="rounded-full bg-muted px-2 py-1 text-xs">
                      {identity.status === 'pending' ? 'Pending' : 'Connected'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="cursor-pointer text-destructive hover:text-destructive"
                      onClick={() => onDisconnect(identity.id)}
                    >
                      <Trash2 className="size-4" />
                      Disconnect
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground flex items-start gap-3">
              <Link2 className="size-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">No connected accounts yet</p>
                <p className="text-sm text-muted-foreground">When OpenSocial or SSO identities are available, they'll appear here with options to manage or disconnect.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
